import { useState } from 'react';
import { mockArticles } from '@/mocks/articles';
import { mockImageLibrary, mockAds, mockTemplates } from '@/mocks/editorLayout';
import type { BlockType, EditorPage } from '@/mocks/editorLayout';
import PageManager from './PageManager';

type PanelTab = 'articles' | 'images' | 'ads' | 'pages' | 'templates' | 'ai';

interface EditorLeftPanelProps {
  pages: EditorPage[];
  currentPageId: string;
  onPageSelect: (pageId: string) => void;
  onReorderPages: (newPages: EditorPage[]) => void;
  onAddPage: (section: string, type: EditorPage['type']) => void;
  onDeletePage: (pageId: string) => void;
  onDuplicatePage: (pageId: string) => void;
  onChangePageStatus: (pageId: string, status: EditorPage['status']) => void;
  onAddBlock: (type: BlockType, content?: Record<string, unknown>) => void;
  onApplyTemplate: (templateId: string) => void;
  onAIAction: (action: string) => void;
}

export default function EditorLeftPanel({
  pages,
  currentPageId,
  onPageSelect,
  onReorderPages,
  onAddPage,
  onDeletePage,
  onDuplicatePage,
  onChangePageStatus,
  onAddBlock,
  onApplyTemplate,
  onAIAction,
}: EditorLeftPanelProps) {
  const [activeTab, setActiveTab] = useState<PanelTab>('pages');
  const [searchQuery, setSearchQuery] = useState('');

  const tabs: { id: PanelTab; icon: string; label: string }[] = [
    { id: 'pages', icon: 'ri-pages-line', label: 'Páginas' },
    { id: 'articles', icon: 'ri-file-text-line', label: 'Artigos' },
    { id: 'images', icon: 'ri-image-line', label: 'Imagens' },
    { id: 'ads', icon: 'ri-advertisement-line', label: 'Anúncios' },
    { id: 'templates', icon: 'ri-layout-grid-line', label: 'Templates' },
    { id: 'ai', icon: 'ri-robot-line', label: 'IA' },
  ];

  const filteredArticles = mockArticles.filter(
    (a) =>
      a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.section.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const statusColor: Record<string, string> = {
    aprovado: 'text-green-700 border-green-600',
    revisão: 'text-yellow-700 border-yellow-600',
    rascunho: 'text-[#999999] border-[#999999]',
    publicado: 'text-green-700 border-green-600',
  };

  return (
    <div className="w-56 flex-shrink-0 bg-white border-r border-[#E0E0E0] flex flex-col h-full overflow-hidden">
      {/* Tab Icons */}
      <div className="flex border-b border-[#E0E0E0] overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            title={tab.label}
            className={`flex-1 min-w-0 flex flex-col items-center justify-center py-2 gap-0.5 cursor-pointer transition-colors border-b-2 ${
              activeTab === tab.id
                ? 'border-[#C41230] text-[#C41230] bg-[#FFF5F7]'
                : 'border-transparent text-[#999999] hover:text-[#444444] hover:bg-[#F8F8F8]'
            }`}
          >
            <i className={`${tab.icon} text-sm`}></i>
            <span className="text-[8px] font-sans font-semibold uppercase tracking-wide hidden sm:block">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Panel Content */}
      <div className="flex-1 overflow-y-auto">

        {/* PAGES — handled by PageManager */}
        {activeTab === 'pages' && (
          <PageManager
            pages={pages}
            currentPageId={currentPageId}
            onPageSelect={onPageSelect}
            onReorderPages={onReorderPages}
            onAddPage={onAddPage}
            onDeletePage={onDeletePage}
            onDuplicatePage={onDuplicatePage}
            onChangePageStatus={onChangePageStatus}
          />
        )}

        {/* ARTICLES */}
        {activeTab === 'articles' && (
          <div className="p-3">
            <input
              type="text"
              placeholder="Buscar artigos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full border border-[#E0E0E0] px-2 py-1.5 text-xs font-sans mb-3 focus:outline-none focus:border-[#111111]"
            />
            <div className="space-y-2">
              {filteredArticles.map((article) => (
                <div
                  key={article.id}
                  className="border border-[#E0E0E0] p-2 hover:border-[#111111] cursor-grab transition-colors group"
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('blockType', 'article');
                    e.dataTransfer.setData('articleId', article.id);
                  }}
                  onClick={() => onAddBlock('article', {
                    title: article.title,
                    body: article.excerpt,
                    author: article.author,
                    section: article.section,
                    imageUrl: article.image,
                    priority: article.priority === 'alta' ? 'high' : article.priority === 'média' ? 'medium' : 'low',
                  })}
                >
                  <div className="flex items-start gap-1.5 mb-1">
                    <span className="text-[8px] font-bold text-[#C41230] border border-[#C41230] px-1 py-0.5 font-sans uppercase tracking-wide whitespace-nowrap">{article.section}</span>
                    <span className={`text-[8px] font-bold border px-1 py-0.5 font-sans uppercase tracking-wide whitespace-nowrap ${statusColor[article.status] || 'text-[#999999] border-[#999999]'}`}>{article.status}</span>
                  </div>
                  <p className="text-[10px] font-bold text-[#111111] font-serif leading-tight line-clamp-2">{article.title}</p>
                  <p className="text-[9px] text-[#999999] font-sans mt-0.5">{article.author}</p>
                  <div className="mt-1.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-drag-move-line text-[#999999] text-xs"></i>
                    <span className="text-[9px] text-[#999999] font-sans">Arrastar para canvas</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* IMAGES */}
        {activeTab === 'images' && (
          <div className="p-3">
            <button
              onClick={() => onAIAction('suggest-images')}
              className="w-full flex items-center gap-2 px-3 py-2 bg-[#111111] text-white text-xs font-sans font-semibold mb-3 cursor-pointer hover:bg-[#333333] transition-colors"
            >
              <i className="ri-robot-line text-sm"></i>
              Sugerir com IA
            </button>
            <div className="grid grid-cols-2 gap-2">
              {mockImageLibrary.map((img) => (
                <div
                  key={img.id}
                  className="cursor-grab group relative"
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('blockType', 'image');
                    e.dataTransfer.setData('imageUrl', img.url);
                  }}
                  onClick={() => onAddBlock('image', { imageUrl: img.url, caption: img.caption })}
                >
                  <div className="w-full aspect-video overflow-hidden border border-[#E0E0E0] group-hover:border-[#111111] transition-colors">
                    <img src={img.url} alt={img.caption} className="w-full h-full object-cover object-top" />
                  </div>
                  <p className="text-[9px] text-[#666666] font-sans mt-0.5 truncate">{img.caption}</p>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                    <i className="ri-add-circle-line text-white text-xl opacity-0 group-hover:opacity-100 transition-opacity"></i>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ADS */}
        {activeTab === 'ads' && (
          <div className="p-3">
            <div className="space-y-2">
              {mockAds.map((ad) => (
                <div
                  key={ad.id}
                  className="border border-[#E0E0E0] p-2 hover:border-[#111111] cursor-grab transition-colors group"
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('blockType', 'ad');
                    e.dataTransfer.setData('adId', ad.id);
                  }}
                  onClick={() => onAddBlock('ad', { adName: ad.name, adClient: ad.client, imageUrl: ad.imageUrl })}
                >
                  <div className="w-full h-16 overflow-hidden border border-[#E0E0E0] mb-1.5">
                    <img src={ad.imageUrl} alt={ad.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <p className="text-[10px] font-bold text-[#111111] font-sans">{ad.name}</p>
                  <div className="flex items-center justify-between mt-0.5">
                    <p className="text-[9px] text-[#999999] font-sans">{ad.size}</p>
                    <span className={`text-[8px] font-bold border px-1 py-0.5 font-sans uppercase ${ad.status === 'ativo' ? 'border-green-600 text-green-600' : 'border-yellow-600 text-yellow-600'}`}>
                      {ad.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TEMPLATES */}
        {activeTab === 'templates' && (
          <div className="p-3">
            {['capa', 'interna', 'contracapa'].map((cat) => (
              <div key={cat} className="mb-4">
                <p className="text-[9px] font-bold text-[#999999] uppercase tracking-wide font-sans mb-2 capitalize">{cat}</p>
                <div className="grid grid-cols-2 gap-2">
                  {mockTemplates.filter((t) => t.category === cat).map((tpl) => (
                    <button
                      key={tpl.id}
                      onClick={() => onApplyTemplate(tpl.id)}
                      className="text-left group cursor-pointer"
                    >
                      <div className="w-full aspect-[3/4] overflow-hidden border border-[#E0E0E0] group-hover:border-[#C41230] transition-colors">
                        <img src={tpl.thumbnail} alt={tpl.name} className="w-full h-full object-cover object-top" />
                      </div>
                      <p className="text-[9px] font-bold text-[#111111] font-sans mt-1 leading-tight">{tpl.name}</p>
                      <p className="text-[8px] text-[#999999] font-sans leading-tight">{tpl.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* AI */}
        {activeTab === 'ai' && (
          <div className="p-3">
            <div className="bg-[#111111] text-white p-3 mb-3">
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-robot-line text-white text-sm"></i>
                <p className="text-xs font-bold font-serif">Motor de IA</p>
              </div>
              <p className="text-[10px] text-white/60 font-sans">Assistente editorial inteligente</p>
            </div>
            <div className="space-y-2">
              {[
                { action: 'generate-layout', icon: 'ri-layout-grid-line', label: 'Gerar layout automático', desc: 'Cria layout completo da página' },
                { action: 'generate-front-page', icon: 'ri-newspaper-line', label: 'Criar capa automática', desc: 'Gera capa com manchetes do dia' },
                { action: 'summarize', icon: 'ri-file-reduce-line', label: 'Resumir artigos', desc: 'Condensa textos longos' },
                { action: 'generate-headlines', icon: 'ri-heading', label: 'Gerar manchetes', desc: 'Cria títulos impactantes' },
                { action: 'suggest-images', icon: 'ri-image-ai-line', label: 'Sugerir imagens', desc: 'Recomenda fotos por contexto' },
                { action: 'optimize-balance', icon: 'ri-scales-line', label: 'Otimizar equilíbrio', desc: 'Balanceia espaços e pesos' },
                { action: 'check-grammar', icon: 'ri-edit-2-line', label: 'Revisar gramática', desc: 'Corrige erros de texto' },
                { action: 'generate-subheadlines', icon: 'ri-text', label: 'Gerar subtítulos', desc: 'Cria subtítulos complementares' },
              ].map((item) => (
                <button
                  key={item.action}
                  onClick={() => onAIAction(item.action)}
                  className="w-full flex items-start gap-2.5 p-2.5 border border-[#E0E0E0] hover:border-[#111111] hover:bg-[#F8F8F8] cursor-pointer transition-colors text-left group"
                >
                  <div className="w-7 h-7 flex items-center justify-center bg-[#F0F0F0] group-hover:bg-[#111111] transition-colors flex-shrink-0">
                    <i className={`${item.icon} text-sm text-[#444444] group-hover:text-white transition-colors`}></i>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-[#111111] font-sans">{item.label}</p>
                    <p className="text-[9px] text-[#999999] font-sans">{item.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
